"use client";

import { useRef } from "react";
import { ArrowRight, Monitor } from "lucide-react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
gsap.registerPlugin(useGSAP, ScrollTrigger);
export default function Welcome() {
  const container = useRef<HTMLElement>(null);
  useGSAP(() => {
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(".hero-eyebrow", {
        y: -16
      });
      gsap.set(".hero-headline", {
        y: 24
      });
      gsap.set(".hero-sub", {
        y: 20
      });
      gsap.set(".hero-cta", {
        y: 16
      });
      gsap.set(".hero-sticker", {
        y: 20,
        scale: 0.7,
        rotate: -20
      });
      const tl = gsap.timeline({
        defaults: {
          ease: "power2.out"
        }
      });
      tl.to(".hero-eyebrow", {
        opacity: 1,
        y: 0,
        duration: 0.7
      }).to(".hero-headline", {
        opacity: 1,
        y: 0,
        duration: 0.9
      }, "-=0.4").to(".hero-sticker", {
        opacity: 1,
        y: 0,
        scale: 1,
        rotate: 0,
        duration: 0.7,
        ease: "back.out(1.8)"
      }, "-=0.5").to(".hero-sub", {
        opacity: 1,
        y: 0,
        duration: 0.7
      }, "-=0.5").to(".hero-cta", {
        opacity: 1,
        y: 0,
        duration: 0.6,
        stagger: 0.15
      }, "-=0.3");
      gsap.to(".layer-sky", {
        yPercent: -8,
        ease: "none",
        scrollTrigger: {
          trigger: container.current,
          start: "top top",
          end: "bottom top",
          scrub: true
        }
      });
      gsap.to(".layer-foliage", {
        yPercent: -14,
        ease: "none",
        scrollTrigger: {
          trigger: container.current,
          start: "top top",
          end: "bottom top",
          scrub: true
        }
      });
      gsap.to(".layer-melo", {
        yPercent: -22,
        ease: "none",
        scrollTrigger: {
          trigger: container.current,
          start: "top top",
          end: "bottom top",
          scrub: true
        }
      });
      const refresh = () => ScrollTrigger.refresh();
      requestAnimationFrame(refresh);
      if (document.readyState === "complete") refresh();else window.addEventListener("load", refresh, {
        once: true
      });
      document.fonts?.ready.then(refresh);
    });
    mm.add("(min-width: 768px) and (prefers-reduced-motion: no-preference)", () => {
      gsap.to(".cloud-drift-a", {
        y: 8,
        duration: 6,
        ease: "sine.inOut",
        yoyo: true,
        repeat: -1
      });
      gsap.to(".cloud-drift-b", {
        y: -8,
        duration: 7,
        ease: "sine.inOut",
        yoyo: true,
        repeat: -1
      });
      gsap.to(".hero-sticker", {
        y: -6,
        rotate: 4,
        duration: 4,
        ease: "sine.inOut",
        yoyo: true,
        repeat: -1
      });
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set([".hero-eyebrow", ".hero-headline", ".hero-sub", ".hero-cta", ".hero-sticker"], {
        opacity: 1,
        y: 0,
        scale: 1,
        rotate: 0
      });
    });
  }, {
    scope: container
  });
  return <section data-section-id="33485" ref={container} id="welcome-to-screenado" className="bg-background text-foreground relative overflow-hidden font-default">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0">
        <div className="layer-sky absolute inset-0">
          <div className="cloud-drift-a absolute left-[6%] top-[18%] h-32 w-56 rounded-lg bg-[var(--muted)]/25 blur-2xl" />
          <div className="cloud-drift-b absolute right-[8%] top-[10%] h-40 w-72 rounded-lg bg-[var(--secondary)]/20 blur-3xl" />
          <div className="cloud-drift-a absolute left-[38%] top-[6%] h-24 w-44 rounded-lg bg-[var(--primary)]/8 blur-2xl" />
          <div className="cloud-drift-b absolute right-[22%] bottom-[28%] h-56 w-96 rounded-lg bg-[var(--primary)]/10 blur-3xl" />
        </div>
        <div className="layer-foliage absolute -right-24 -top-24 h-[520px] w-[520px] rounded-lg bg-[var(--secondary)]/15 blur-3xl" />
        <div className="layer-foliage absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[var(--background)] via-[var(--background)]/70 to-transparent" />
      </div>

      <div className="relative z-10 mx-auto flex min-h-[560px] w-full max-w-[1240px] flex-col items-center px-6 pb-24 pt-32 text-center sm:px-8 md:min-h-[640px] md:pb-32 md:pt-40 lg:pt-48">
        <div className="mx-auto max-w-3xl">
          <h1 className="hero-headline mt-6 font-serif text-4xl font-normal leading-[1.02] tracking-tight text-foreground sm:text-6xl md:text-7xl lg:text-[5.5rem]" style={{
          opacity: 0
        }}>
            <span className="!font-sans">
              <span className="!text-6xl !font-semibold"><span className="!text-6xl">Screenado</span></span>
            </span>
            <span className="relative inline-block">
              <span aria-hidden="true" className="hero-sticker absolute -right-6 -top-2 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-[var(--primary)] text-[var(--primary-foreground)] shadow-lg sm:-right-12 sm:-top-6 sm:h-12 sm:w-12">
                <Monitor className="h-5 w-5 sm:h-6 sm:w-6" aria-hidden="true" />
              </span>
            </span>
          </h1>

          <p className="hero-sub mx-auto mt-8 max-w-2xl text-lg leading-relaxed text-muted-foreground sm:text-xl" style={{
          opacity: 0
        }}>
            <span className="text-lg sm:!text-2xl">
              <span className="sm:!text-3xl">
                <span className="sm:!text-4xl">Parenting is hard. The internet is weird. We&apos;re here to help.</span>
              </span>
            </span>
          </p>

          <div className="mt-10 flex flex-col items-stretch justify-center gap-4 sm:flex-row sm:items-center">
            <Button asChild size="lg" className="hero-cta h-auto rounded-lg bg-[var(--primary)] px-8 py-4 text-base font-semibold text-[var(--primary-foreground)] shadow-lg hover:bg-[var(--primary)]/90" style={{
            opacity: 0
          }}>
              <Link to="/WhatAreKidsWatchingPage">{"Start Exploring"}
                <ArrowRight className="ml-1 h-4 w-4" /></Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="hero-cta h-auto rounded-lg border-2 border-[var(--primary)] bg-transparent px-8 py-4 text-base font-semibold text-[var(--primary)] shadow-lg hover:bg-[var(--primary)]/10 hover:text-[var(--primary)]" style={{
            opacity: 0
          }}>
              
            </Button>
          </div>
        </div>

        <div className="layer-melo relative mt-16 w-full max-w-4xl md:mt-20">
          <div className="relative overflow-hidden rounded-3xl">
            <img src="https://screenado.com/wp-content/uploads/2026/08/Screenado-Homepage-Image-1.png" data-wvc-srcset="https://screenado.com/wp-content/uploads/2026/08/Screenado-Homepage-Image-1.png-wvc-srcset" data-wvc-sizes="https://screenado.com/wp-content/uploads/2026/08/Screenado-Homepage-Image-1.png-wvc-sizes" alt="A parent and child sitting together in a cozy living room, looking at a tablet with a calm and engaged expression" className="h-auto w-full object-cover" loading="eager" decoding="async" />
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-[var(--background)] to-transparent" />
          </div>
        </div>
      </div>
    </section>;
}